/**
 * Outil « Robot sur quadrillage » (Boîte à outils, 6e → 4e).
 *
 * On assemble un programme de blocs (avancer, tourner, ou aller dans une
 * direction ; répéter n fois), le robot l'exécute case par case en traçant
 * son chemin, l'instruction en cours est surlignée. Des défis (atteindre une
 * étoile, contourner un mur, tracer un carré avec une boucle, un escalier) et
 * « Prévoir » : lire un programme et deviner où le robot s'arrête.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowDown, ArrowLeft, ArrowRight, ArrowUp, Minus, Plus, Trash2 } from 'lucide-react';
import { cn } from '@jeraisonne/ui';
import type { ToolProps } from '../../tool-types';
import { Note, PRIMARY_BUTTON, SECONDARY_BUTTON, Segmented, Stepper } from '../../ui/controls';
import { frenchSpaces } from '../../ui/format';
import { FIGURE_BG_CLASS } from '../../ui/svg-parts';
import {
  CHALLENGES,
  DIR_NAME,
  PREDICT,
  SIZE,
  addAt,
  instrText,
  judge,
  moveAt,
  removeAt,
  targetAfterMove,
  targetAfterRemove,
  run,
  segKey,
  updateAt,
  type Dir,
  type Instr,
  type Path,
  type RState,
} from './robot';

const CELL = 40;
const W = SIZE * CELL;
/** marge à gauche et en bas pour numéroter les colonnes et les lignes */
const M = 18;
const cx = (x: number) => x * CELL + CELL / 2;
const cy = (y: number) => (SIZE - 1 - y) * CELL + CELL / 2;
const samePath = (a: Path | null | undefined, b: Path) =>
  !!a && a.length === b.length && a.every((v, i) => v === b[i]);
const ROBOT = 'var(--color-primary-500)';
const FREE_START: RState = { x: 0, y: 0, dir: 0 };
const TRAIL = 'var(--color-primary-400)';

function Board({
  state,
  segments,
  goal,
  walls,
  draw,
  guess,
  onCell,
  label,
}: {
  state: RState;
  segments: readonly string[];
  goal?: { x: number; y: number };
  walls?: readonly string[];
  draw?: readonly string[];
  guess?: { x: number; y: number } | null;
  onCell?: (x: number, y: number) => void;
  label: string;
}) {
  const svgRef = useRef<SVGSVGElement>(null);
  const seg = (k: string) => {
    const [a, b] = k.split('|').map((s) => s.split(',').map(Number)) as [number[], number[]];
    return { x1: cx(a[0]!), y1: cy(a[1]!), x2: cx(b[0]!), y2: cy(b[1]!) };
  };
  const angle = -90 * state.dir; // 0 : vers la droite
  return (
    <div className="mx-auto w-full max-w-[26rem]">
      <svg
        ref={svgRef}
        viewBox={`${-M} 0 ${W + M} ${W + M}`}
        className={cn(
          'border-ink-100 block aspect-square w-full touch-none select-none rounded-2xl border',
          FIGURE_BG_CLASS,
          onCell && 'cursor-pointer',
        )}
        role="img"
        aria-label={label}
        onPointerDown={(e) => {
          if (!onCell || !svgRef.current) return;
          const r = svgRef.current.getBoundingClientRect();
          const px = ((e.clientX - r.left) / r.width) * (W + M) - M;
          const py = ((e.clientY - r.top) / r.height) * (W + M);
          const x = Math.floor(px / CELL);
          const y = SIZE - 1 - Math.floor(py / CELL);
          if (x >= 0 && y >= 0 && x < SIZE && y < SIZE) onCell(x, y);
        }}
      >
        {Array.from({ length: SIZE + 1 }, (_, i) => (
          <g key={i} aria-hidden>
            <line x1={i * CELL} y1={0} x2={i * CELL} y2={W} stroke="var(--color-ink-100)" />
            <line x1={0} y1={i * CELL} x2={W} y2={i * CELL} stroke="var(--color-ink-100)" />
          </g>
        ))}
        {Array.from({ length: SIZE }, (_, i) => (
          <g
            key={`n${i}`}
            aria-hidden
            fontSize={12}
            fill="var(--color-ink-500)"
            textAnchor="middle"
          >
            <text x={cx(i)} y={W + 13}>
              {i}
            </text>
            <text x={-9} y={cy(i) + 4}>
              {i}
            </text>
          </g>
        ))}
        {walls?.map((k) => {
          const [x, y] = k.split(',').map(Number) as [number, number];
          return (
            <rect
              key={k}
              x={x * CELL + 2}
              y={(SIZE - 1 - y) * CELL + 2}
              width={CELL - 4}
              height={CELL - 4}
              rx={4}
              fill="var(--color-ink-500)"
              aria-hidden
            />
          );
        })}
        {draw?.map((k) => (
          <line
            key={k}
            {...seg(k)}
            stroke="var(--color-accent-500)"
            strokeWidth={4}
            strokeDasharray="6 6"
            strokeLinecap="round"
            aria-hidden
          />
        ))}
        {segments.map((k) => (
          <line
            key={k}
            {...seg(k)}
            stroke={TRAIL}
            strokeWidth={5}
            strokeLinecap="round"
            aria-hidden
          />
        ))}
        {goal && (
          <text
            x={cx(goal.x)}
            y={cy(goal.y)}
            textAnchor="middle"
            dominantBaseline="central"
            fontSize={26}
            fill="var(--color-accent-500)"
            aria-hidden
          >
            ★
          </text>
        )}
        {guess && (
          <rect
            x={guess.x * CELL + 3}
            y={(SIZE - 1 - guess.y) * CELL + 3}
            width={CELL - 6}
            height={CELL - 6}
            rx={6}
            fill="none"
            stroke="var(--color-accent-500)"
            strokeWidth={3}
            aria-hidden
          />
        )}
        <g
          transform={`translate(${cx(state.x)} ${cy(state.y)}) rotate(${angle})`}
          aria-hidden
          data-testid="robot"
        >
          <circle r={14} fill={ROBOT} />
          <polygon points="16,0 4,-8 4,8" fill="white" />
        </g>
      </svg>
    </div>
  );
}

function NStepper({
  value,
  onChange,
  label,
}: {
  value: number;
  onChange: (n: number) => void;
  label: string;
}) {
  return (
    <span className="flex items-center">
      <button
        type="button"
        aria-label={`${label} : moins`}
        disabled={value <= 1}
        onClick={() => onChange(value - 1)}
        className="text-ink-700 hover:bg-ink-100 flex min-h-11 min-w-11 items-center justify-center rounded-xl disabled:opacity-40"
      >
        <Minus className="h-4 w-4" aria-hidden />
      </button>
      <span className="text-ink-900 w-6 text-center font-bold tabular-nums">{value}</span>
      <button
        type="button"
        aria-label={`${label} : plus`}
        disabled={value >= 9}
        onClick={() => onChange(value + 1)}
        className="text-ink-700 hover:bg-ink-100 flex min-h-11 min-w-11 items-center justify-center rounded-xl disabled:opacity-40"
      >
        <Plus className="h-4 w-4" aria-hidden />
      </button>
    </span>
  );
}

function ProgramList({
  program,
  base,
  current,
  editable,
  target,
  onChange,
  onTarget,
  onRemove,
  onMoveUp,
}: {
  program: readonly Instr[];
  base: Path;
  current: Path | null;
  editable: boolean;
  target: Path;
  onChange?: (f: (p: Instr[]) => Instr[]) => void;
  onTarget?: (p: Path) => void;
  onRemove?: (p: Path) => void;
  onMoveUp?: (p: Path) => void;
}) {
  return (
    <ol className={cn('space-y-1', base.length > 0 && 'border-primary-200 ml-3 border-l-4 pl-2')}>
      {program.map((ins, i) => {
        const path = [...base, i];
        const active = samePath(current, path);
        const name = `Bloc ${path.map((v) => v + 1).join('.')}, ${instrText(ins)}`;
        return (
          <li key={i}>
            <div
              className={cn(
                'flex min-h-11 items-center gap-1 rounded-xl border px-2',
                active ? 'border-primary-500 bg-primary-50' : 'border-ink-100',
              )}
              aria-current={active ? 'step' : undefined}
            >
              <span className="text-ink-900 min-w-0 flex-1 text-sm font-semibold">
                {ins.k === 'fwd'
                  ? 'avancer de'
                  : ins.k === 'move'
                    ? `aller vers ${DIR_NAME[ins.dir]} de`
                    : ins.k === 'repeat'
                      ? 'répéter'
                      : instrText(ins)}
                {!editable && 'n' in ins && <span className="tabular-nums"> {ins.n}</span>}
                {!editable && ins.k === 'repeat' && ' fois'}
              </span>
              {editable && 'n' in ins && (
                <NStepper
                  value={ins.n}
                  label={name}
                  onChange={(n) =>
                    onChange?.((p) => updateAt(p, path, (x) => ({ ...x, n }) as Instr))
                  }
                />
              )}
              {editable && ins.k === 'repeat' && <span className="text-ink-700 text-sm">fois</span>}
              {editable && (
                <button
                  type="button"
                  aria-label={`Monter : ${name}`}
                  disabled={i === 0}
                  onClick={() => onMoveUp?.(path)}
                  className="text-ink-500 hover:bg-ink-100 flex min-h-11 min-w-11 items-center justify-center rounded-xl disabled:opacity-30"
                >
                  <ArrowUp className="h-4 w-4" aria-hidden />
                </button>
              )}
              {editable && (
                <button
                  type="button"
                  aria-label={`Retirer : ${name}`}
                  onClick={() => onRemove?.(path)}
                  className="text-ink-500 hover:bg-ink-100 flex min-h-11 min-w-11 items-center justify-center rounded-xl"
                >
                  <Trash2 className="h-4 w-4" aria-hidden />
                </button>
              )}
            </div>
            {ins.k === 'repeat' && (
              <div className="mt-1">
                <ProgramList
                  program={ins.body}
                  base={path}
                  current={current}
                  editable={editable}
                  target={target}
                  onChange={onChange}
                  onTarget={onTarget}
                  onRemove={onRemove}
                  onMoveUp={onMoveUp}
                />
                {editable && (
                  <button
                    type="button"
                    aria-pressed={samePath(target, path)}
                    onClick={() => onTarget?.(samePath(target, path) ? [] : path)}
                    className={cn(
                      'ml-3 mt-1 min-h-11 rounded-xl border border-dashed px-3 text-xs font-semibold',
                      samePath(target, path)
                        ? 'border-primary-500 bg-primary-50 text-primary-700'
                        : 'border-ink-300 text-ink-600',
                    )}
                  >
                    {samePath(target, path)
                      ? 'Les blocs vont dans cette boucle'
                      : 'Ajouter dans cette boucle'}
                  </button>
                )}
              </div>
            )}
          </li>
        );
      })}
    </ol>
  );
}

type Blocks = 'relative' | 'absolute';

function Palette({ blocks, onAdd }: { blocks: Blocks; onAdd: (i: Instr) => void }) {
  const b =
    'border-ink-200 text-ink-800 hover:bg-ink-50 flex min-h-11 items-center gap-1 rounded-xl border px-3 text-sm font-semibold';
  return (
    <div role="group" aria-label="Blocs à ajouter" className="flex flex-wrap gap-2">
      {blocks === 'relative' ? (
        <>
          <button type="button" className={b} onClick={() => onAdd({ k: 'fwd', n: 1 })}>
            Avancer
          </button>
          <button type="button" className={b} onClick={() => onAdd({ k: 'left' })}>
            Tourner à gauche
          </button>
          <button type="button" className={b} onClick={() => onAdd({ k: 'right' })}>
            Tourner à droite
          </button>
        </>
      ) : (
        ([1, 3, 2, 0] as Dir[]).map((d) => {
          const Icon = [ArrowRight, ArrowUp, ArrowLeft, ArrowDown][d]!;
          return (
            <button
              key={d}
              type="button"
              className={b}
              aria-label={`Aller vers ${DIR_NAME[d]}`}
              onClick={() => onAdd({ k: 'move', dir: d, n: 1 })}
            >
              <Icon className="h-4 w-4" aria-hidden />
            </button>
          );
        })
      )}
      <button type="button" className={b} onClick={() => onAdd({ k: 'repeat', n: 2, body: [] })}>
        Répéter
      </button>
    </div>
  );
}

/** déroulé animé d'une exécution */
function usePlayer(frames: number) {
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(false);
  useEffect(() => {
    if (!playing) return;
    if (idx >= frames - 1) {
      setPlaying(false);
      return;
    }
    const t = setTimeout(() => setIdx((i) => i + 1), 260);
    return () => clearTimeout(t);
  }, [playing, idx, frames]);
  return { idx: Math.min(idx, frames - 1), setIdx, playing, setPlaying };
}

function RunControls({
  player,
  last,
  onReset,
}: {
  player: ReturnType<typeof usePlayer>;
  last: number;
  onReset?: () => void;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      <button
        type="button"
        className={PRIMARY_BUTTON}
        onClick={() => {
          if (player.idx >= last) player.setIdx(0);
          player.setPlaying(true);
        }}
      >
        Exécuter
      </button>
      <button
        type="button"
        className={SECONDARY_BUTTON}
        disabled={player.idx >= last}
        onClick={() => {
          player.setPlaying(false);
          player.setIdx(player.idx + 1);
        }}
      >
        Un pas
      </button>
      <button
        type="button"
        className={SECONDARY_BUTTON}
        onClick={() => {
          player.setPlaying(false);
          player.setIdx(0);
          onReset?.();
        }}
      >
        Revenir au départ
      </button>
    </div>
  );
}

function Builder({ challengeId }: { challengeId: string | null }) {
  const c = CHALLENGES.find((x) => x.id === challengeId) ?? null;
  const start: RState = c?.start ?? FREE_START;
  const walls = useMemo(() => new Set(c?.walls ?? []), [c]);
  const [program, setProgram] = useState<Instr[]>([]);
  const [target, setTarget] = useState<Path>([]);
  const [blocks, setBlocks] = useState<Blocks>('relative');
  const r = useMemo(() => run(program, start, walls), [program, start, walls]);
  const player = usePlayer(r.frames.length);
  const last = r.frames.length - 1;
  const frame = r.frames[player.idx]!;
  // tracé jusqu'à l'image en cours
  const shown = useMemo(() => {
    const out = new Set<string>();
    for (let i = 1; i <= player.idx; i++) {
      const a = r.frames[i - 1]!.state;
      const b = r.frames[i]!.state;
      if (a.x !== b.x || a.y !== b.y) out.add(segKey(a, b));
    }
    return [...out];
  }, [r, player.idx]);
  const done = player.idx >= last && last > 0;
  const verdict = c && done ? judge(c, program) : null;
  const edit = (f: (p: Instr[]) => Instr[]) => {
    setProgram((p) => f(p));
    player.setPlaying(false);
    player.setIdx(0);
  };

  return (
    <div className="space-y-4">
      {c && <Note live={false}>{frenchSpaces(c.text)}</Note>}
      <Board
        state={frame.state}
        segments={shown}
        goal={c?.goal}
        walls={c?.walls}
        draw={c?.draw}
        label={`Robot en (${frame.state.x} ; ${frame.state.y}), tourné vers ${DIR_NAME[frame.state.dir]}`}
      />
      <p className="sr-only" aria-live="polite">
        {done && r.error ? r.error : verdict ? verdict.say : ''}
      </p>
      <RunControls player={player} last={last} />
      {frame.lap && (
        <p className="text-ink-600 text-sm" data-testid="rb-lap">
          {frenchSpaces(`Boucle : tour ${frame.lap.i} sur ${frame.lap.n}.`)}
        </p>
      )}
      {done && r.error && (
        <Note tone="error" live={false}>
          {frenchSpaces(r.error)}
        </Note>
      )}
      {verdict && (
        <Note tone={verdict.ok ? 'success' : 'error'} live={false}>
          {frenchSpaces(verdict.say)}
        </Note>
      )}
      <section className="space-y-2" aria-label="Programme" data-testid="rb-program">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-ink-600 text-sm font-semibold">Programme</h3>
          <Segmented
            ariaLabel="Sorte de blocs"
            value={blocks}
            options={[
              { value: 'relative', label: 'Tourner' },
              { value: 'absolute', label: 'Directions' },
            ]}
            onChange={setBlocks}
          />
        </div>
        {program.length === 0 ? (
          <p className="text-ink-500 text-sm">Ajoute des blocs avec les boutons ci-dessous.</p>
        ) : (
          <ProgramList
            program={program}
            base={[]}
            current={player.idx > 0 ? frame.path : null}
            editable
            target={target}
            onChange={edit}
            onTarget={setTarget}
            onRemove={(path) => {
              edit((p) => removeAt(p, path));
              setTarget((t) => targetAfterRemove(t, path));
            }}
            onMoveUp={(path) => {
              edit((p) => moveAt(p, path, -1));
              setTarget((t) => targetAfterMove(t, path, -1));
            }}
          />
        )}
        <Palette
          blocks={blocks}
          onAdd={(ins) => {
            edit((p) => addAt(p, target, ins));
          }}
        />
        {program.length > 0 && (
          <button
            type="button"
            className={SECONDARY_BUTTON}
            onClick={() => {
              edit(() => []);
              setTarget([]);
            }}
          >
            Tout effacer
          </button>
        )}
      </section>
    </div>
  );
}

function Predict() {
  const [k, setK] = useState(0);
  const [guess, setGuess] = useState<{ x: number; y: number } | null>(null);
  const [checked, setChecked] = useState(false);
  const p = PREDICT[k]!;
  const r = useMemo(() => run(p.program, p.start), [p]);
  const player = usePlayer(r.frames.length);
  const last = r.frames.length - 1;
  const frame = checked ? r.frames[player.idx]! : r.frames[0]!;
  const end = r.frames[last]!.state;
  const shown = useMemo(() => {
    if (!checked) return [];
    const out = new Set<string>();
    for (let i = 1; i <= player.idx; i++) {
      const a = r.frames[i - 1]!.state;
      const b = r.frames[i]!.state;
      if (a.x !== b.x || a.y !== b.y) out.add(segKey(a, b));
    }
    return [...out];
  }, [r, player.idx, checked]);
  const right = guess && guess.x === end.x && guess.y === end.y;
  const finished = checked && player.idx >= last;

  return (
    <div className="space-y-4">
      <Note live={false}>
        {frenchSpaces(
          `Le robot part de la case (${p.start.x} ; ${p.start.y}), tourné vers ${DIR_NAME[p.start.dir]}. Lis le programme, puis touche la case où il s’arrêtera.`,
        )}
      </Note>
      <section className="border-ink-100 rounded-2xl border p-2" aria-label="Programme à lire">
        <ProgramList
          program={p.program}
          base={[]}
          current={checked && player.idx > 0 ? frame.path : null}
          editable={false}
          target={[]}
        />
      </section>
      <Board
        state={frame.state}
        segments={shown}
        guess={guess}
        onCell={checked ? undefined : (x, y) => setGuess({ x, y })}
        label={`Robot en (${frame.state.x} ; ${frame.state.y})${guess ? `, ta case : (${guess.x} ; ${guess.y})` : ''}`}
      />
      <p className="text-ink-600 text-sm" aria-live="polite">
        {frenchSpaces(
          guess
            ? `Ta case : (${guess.x} ; ${guess.y}).`
            : 'Touche une case du quadrillage, ou choisis sa colonne et sa ligne.',
        )}
      </p>
      {!checked && (
        <div className="grid grid-cols-2 gap-2">
          <Stepper
            label="Colonne"
            value={guess?.x ?? 0}
            min={0}
            max={SIZE - 1}
            onChange={(x) => setGuess({ x, y: guess?.y ?? 0 })}
          />
          <Stepper
            label="Ligne"
            value={guess?.y ?? 0}
            min={0}
            max={SIZE - 1}
            onChange={(y) => setGuess({ x: guess?.x ?? 0, y })}
          />
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          className={PRIMARY_BUTTON}
          disabled={!guess || checked}
          onClick={() => {
            setChecked(true);
            player.setIdx(0);
            player.setPlaying(true);
          }}
        >
          Vérifier
        </button>
        <button
          type="button"
          className={SECONDARY_BUTTON}
          onClick={() => {
            setK((k + 1) % PREDICT.length);
            setGuess(null);
            setChecked(false);
            player.setPlaying(false);
            player.setIdx(0);
          }}
        >
          Autre programme
        </button>
      </div>
      {finished && (
        <Note tone={right ? 'success' : 'error'}>
          {frenchSpaces(
            right
              ? `Bravo : le robot s’arrête bien en (${end.x} ; ${end.y}).`
              : `Le robot s’arrête en (${end.x} ; ${end.y}). Relis le programme bloc par bloc, en suivant sa direction.`,
          )}
        </Note>
      )}
    </div>
  );
}

type Mode = 'free' | 'challenge' | 'predict';

export function GridRobotTool(_props: ToolProps) {
  const [mode, setMode] = useState<Mode>('free');
  const [cid, setCid] = useState(CHALLENGES[0]!.id);
  return (
    <div className="space-y-4">
      <Segmented
        ariaLabel="Ce qu’on fait"
        value={mode}
        options={[
          { value: 'free', label: 'Libre' },
          { value: 'challenge', label: 'Défis' },
          { value: 'predict', label: 'Prévoir' },
        ]}
        onChange={setMode}
      />
      {mode === 'challenge' && (
        <div role="group" aria-label="Défis" className="flex flex-wrap gap-2">
          {CHALLENGES.map((c) => (
            <button
              key={c.id}
              type="button"
              aria-pressed={c.id === cid}
              onClick={() => setCid(c.id)}
              className={cn(
                'min-h-11 rounded-xl border px-3 text-sm font-semibold',
                c.id === cid
                  ? 'border-primary-500 bg-primary-50 text-primary-700'
                  : 'border-ink-200 text-ink-700 hover:bg-ink-50',
              )}
            >
              {c.title}
            </button>
          ))}
        </div>
      )}
      {mode === 'free' && <Builder key="free" challengeId={null} />}
      {mode === 'challenge' && <Builder key={cid} challengeId={cid} />}
      {mode === 'predict' && <Predict />}
    </div>
  );
}
